import {EventManager, ReactThreeFiber, ThreeElement, useFrame, useThree} from "@react-three/fiber"
import {Camera, Event, OrthographicCamera, PerspectiveCamera} from "three"
import * as THREE from "three";
import {useEffect, useMemo} from "react";
import {Rect, Vec2, Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import _ from "lodash";
import {Assert} from "js-vextensions";
import {GetClientPosFromWorldPos, GetWorldPosFromClientPos} from "../../../../Utils/ThreeJS/SpaceUtils.js";
import {Invalidate3JS} from "../../../../Utils/ThreeJS/Invalidate3JS.js";
import {store} from "../../../../Store/index.js";
import {Tool} from "../Toolbox/Tools/@Tool.js";
import {GetMapState} from "../../../../Store/main/maps/$mapState.js";
import {GetOpenMapID} from "../../../../Store/main/maps.js";
import {GetAsync, RunInAction} from "mobx-graphlink";
import {MapDragInfo} from "../Toolbox/Tools/Helpers/DragHelper.js";
import {ActiveMapTool} from "../../../../Store/main/maps/MapTools.js";
import {RunInAction_Set} from "web-vcore";
import {MapSurface_current} from "../MapSurface.js";
import {GetMapObject} from "../../../../Server/DB/mapObjects.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {ResolveObjPosInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";

export class MapSurfaceClickEvent {
	constructor(data: MapSurfaceClickEvent) { Object.assign(this, data); }
	button!: number;
	worldPos!: Vec3;
}

export type OnClick = (event: MapSurfaceClickEvent)=>any;
export type OnDragStart = (drag: MapDragInfo)=>any;
export type OnDragComplete = (drag: MapDragInfo)=>any;
export type OnCamTransformChanged = (pos: Vec3, zoom: number)=>any;
export const MapControls_React = ((props: {
	camera?: OrthographicCamera | PerspectiveCamera, domElement?: HTMLCanvasElement, options?: MapControlsOptions,
	controlsRef?: (controls: MapControls|n)=>any,
})=>{
	const {camera, domElement, options, controlsRef, ...restProps} = props;

	const defaultCamera = useThree(state=>state.camera);
	const gl = useThree(state=>state.gl);
	//const events = useThree(state=>state.events) as EventManager<HTMLElement>;
	const {invalidate} = useThree();
	const explCamera = (camera || defaultCamera) as OrthographicCamera; // | PerspectiveCamera;
	//const explDomElement = (domElement || events.connected || gl.domElement) as HTMLElement;
	const explDomElement = domElement || gl.domElement;

	//const controls = useMemo(()=>new MapControls(explCamera, explDomElement), [explCamera, domElement])
	useEffect(()=>{
		const controls = new MapControls(explCamera, explDomElement, {...options, invalidate});
		RunInAction("MapControls_React.effect", ()=>store.main.maps.mapControls_current = controls);
		controlsRef?.(controls);
		return ()=>{
			controls.dispose();
			controlsRef?.(null);
			if (store.main.maps.mapControls_current == controls) {
				RunInAction("MapControls_React.effect.disposer", ()=>store.main.maps.mapControls_current = null);
			}
		};
	}, [controlsRef, invalidate, explCamera, explDomElement, options]);

	return null;
});

export class MapControlsOptions {
	constructor(data?: Partial<MapControlsOptions>) { Object.assign(this, data); }

	panSpeed = 1;
	zoomSpeed = .5;
	minZoom = -20; // this is possible because we use a transform `Math.pow(X, zoomLevel)` to convert zoom-levels to camera zoom
	maxZoom = 1000;
	damping = false;
	dampingFactor = 0.05;
	//onCamTransformChanged_throttle = 500; // in ms

	onClick: OnClick|n;
	/*onDragStart: OnDragStart|n;
	onDragComplete: OnDragComplete|n;*/
	onCamTransformChanged: OnCamTransformChanged|n;
	invalidate: (()=>void)|n;
}

/** Gets rescaled/final version of zoom, so that it moves slower at low/far zoom-levels. */
export function TransformZoomLevel(zoomLevel: number, zoomLevel_transformBase: number) {
	return Math.pow(zoomLevel_transformBase, zoomLevel);
}
/** Converts the rescaled/final camera zoom value, back to a linear zoom level. */
export function UntransformTransformedZoomLevel(zoomLevel_transformed: number, zoomLevel_transformBase: number) {
	return Math.log(zoomLevel_transformed) / Math.log(zoomLevel_transformBase);
}

//const presetHeight = 100;

export class MapControls {
	camera: THREE.OrthographicCamera;
	domElement: HTMLCanvasElement;
	//domElement_lastSafeClientRect: DOMRect|n;
	options: MapControlsOptions;
	//onCamTransformChanged_throttled: OnCamTransformChanged|n;

	private currentDrag: MapDragInfo<{prevMousePos: Vec2}>|n;
	zoomLevel = 1;
	targetZoomLevel = 1;
	targetPosition = new Vec2(0, 0);

	/** Base determines how "logarithmic" the zoom feels. Higher values = more scroll wheel turns needed at far zoom levels. */
	zoomLevel_transformBase = 1.5;
	/** Gets rescaled/final version of zoom, so that it moves slower at low/far zoom-levels. */
	get ZoomLevel_Transformed() {
		return TransformZoomLevel(this.zoomLevel, this.zoomLevel_transformBase);
	}
	/** Converts the rescaled/final camera zoom value, back to a linear zoom level. */
	UntransformTransformedZoomLevel(zoomLevel_transformed: number) {
		return UntransformTransformedZoomLevel(zoomLevel_transformed, this.zoomLevel_transformBase);
	}

	//cameraWidth: number;
	//cameraHeight: number;

	constructor(camera: THREE.OrthographicCamera, domElement: HTMLCanvasElement, options?: Partial<MapControlsOptions>) {
		this.camera = camera;
		this.domElement = domElement;
		this.options = Object.assign(new MapControlsOptions(), options);
		//this.onCamTransformChanged_throttled = this.options.onCamTransformChanged == null ? null : _.throttle(this.options.onCamTransformChanged, this.options.onCamTransformChanged_throttle, {leading: true, trailing: true});

		Assert(!isNaN(this.camera.zoom), "Camera zoom cannot be NaN");
		this.targetPosition = Vec2.From3JPos(this.camera.position);
		this.targetZoomLevel = this.UntransformTransformedZoomLevel(this.camera.zoom);
		this.zoomLevel = this.targetZoomLevel;

		// Original camera dimensions
		//this.cameraWidth = this.camera.right - this.camera.left;
		//this.cameraHeight = this.camera.top - this.camera.bottom;

		// Add event listeners
		this.addEventListeners();
	}

	addEventListeners() {
		this.domElement.addEventListener("pointerdown", this.onPointerDown);
		this.domElement.addEventListener("pointermove", this.onPointerMove);
		this.domElement.addEventListener("pointerup", this.onPointerUp);
		window.addEventListener("pointerup", this.onPointerUp_global);
		window.addEventListener("pointermove", this.onPointerMove_global);

		this.domElement.addEventListener("wheel", this.onMouseWheel, {passive: false});

		this.domElement.addEventListener("touchstart", this.onTouchStart, {passive: false});
		window.addEventListener("touchmove", this.onTouchMove_global, {passive: false});
		window.addEventListener("touchend", this.onTouchEndOrCancel_global);
		window.addEventListener("touchcancel", this.onTouchEndOrCancel_global);

		// this ancestor has tabIndex=-1, so can receive key-events (todo: maybe remove these, and just use global handlers that check position of mouse)
		const threeJSContainer = this.domElement.parentElement!.parentElement!.parentElement!;
		threeJSContainer.addEventListener("keydown", this.onKeyDown);
		threeJSContainer.addEventListener("keyup", this.onKeyUp);
		window.addEventListener("keydown", this.onKeyDown_global);
		window.addEventListener("keyup", this.onKeyUp_global);
	}

	removeEventListeners() {
		this.domElement.removeEventListener("pointerdown", this.onPointerDown);
		this.domElement.removeEventListener("pointermove", this.onPointerMove);
		this.domElement.removeEventListener("pointerup", this.onPointerUp);
		window.removeEventListener("pointerup", this.onPointerUp_global);
		window.removeEventListener("pointermove", this.onPointerMove_global);

		this.domElement.removeEventListener("wheel", this.onMouseWheel);

		this.domElement.removeEventListener("touchstart", this.onTouchStart);
		window.removeEventListener("touchmove", this.onTouchMove_global);
		window.removeEventListener("touchend", this.onTouchEndOrCancel_global);
		window.removeEventListener("touchcancel", this.onTouchEndOrCancel_global);

		// this ancestor has tabIndex=-1, so can receive key-events (todo: maybe remove these, and just use global handlers that check position of mouse)
		const threeJSContainer = this.domElement.parentElement?.parentElement?.parentElement;
		threeJSContainer?.removeEventListener("keydown", this.onKeyDown);
		threeJSContainer?.removeEventListener("keyup", this.onKeyUp);
		window.removeEventListener("keydown", this.onKeyDown_global);
		window.removeEventListener("keyup", this.onKeyUp_global);
	}

	CamLRTBZ() { return {left: this.camera.left, right: this.camera.right, top: this.camera.top, bottom: this.camera.bottom, zoom: this.camera.zoom}; }

	/*ClientPosToWorldPos(mousePos: Vec2) {
		if (!this.domElement_lastSafeClientRect) return null;
		return GetWorldPosFromClientPos(mousePos, this.domElement_lastSafeClientRect, Vec3.From3JPos(this.camera.position), this.CamLRTBZ());
	}
	WorldPosToClientPos(worldPos: Vec2) {
		if (!this.domElement_lastSafeClientRect) return null;
		return GetClientPosFromWorldPos(worldPos, this.domElement_lastSafeClientRect, Vec3.From3JPos(this.camera.position), this.CamLRTBZ());
	}
	OneMeterAsPixels() {
		const worldPosX0Y0_clientPos = this.WorldPosToClientPos(Vec2.zero);
		const worldPosX1Y1_clientPos = this.WorldPosToClientPos(Vec2.one);
		if (!worldPosX0Y0_clientPos || !worldPosX1Y1_clientPos) return null;
		return worldPosX1Y1_clientPos.Minus(worldPosX0Y0_clientPos);
	}
	OnePixelAsMeters() {
		const reverse = this.OneMeterAsPixels();
		if (!reverse) return null;
		return new Vec2(1 / reverse.x, 1 / reverse.y);
	}*/
	get DOM() {
		if (!this.domElement.isConnected) return null;
		const clientRect_ = this.domElement.getBoundingClientRect();
		if (!clientRect_) return null;
		const helper = {
			clientRect: clientRect_,
			ClientPosToWorldPos: (mousePos: Vec2)=>{
				return GetWorldPosFromClientPos(mousePos, helper.clientRect, Vec3.From3JPos(this.camera.position), this.CamLRTBZ());
			},
			WorldPosToClientPos: (worldPos: Vec2)=>{
				return GetClientPosFromWorldPos(worldPos, helper.clientRect, Vec3.From3JPos(this.camera.position), this.CamLRTBZ());
			},
			OneMeterAsPixels: ()=>{
				const worldPosX0Y0_clientPos = helper.WorldPosToClientPos(Vec2.zero);
				const worldPosX1Y1_clientPos = helper.WorldPosToClientPos(Vec2.one);
				return worldPosX1Y1_clientPos.Minus(worldPosX0Y0_clientPos);
			},
			OnePixelAsMeters: ()=>{
				const reverse = helper.OneMeterAsPixels();
				return new Vec2(1 / reverse.x, 1 / reverse.y);
			},
		};
		return helper;
	}

	/** Returns true when the canvas dom-element is detached from the document. (eg. when react is disposing of the comp-tree) */
	IgnoreDOMEvents() {
		//return this.domElement.getBoundingClientRect().width == 0 && this.domElement.getBoundingClientRect().height == 0;
		//return this.domElement.offsetWidth == 0 || this.domElement.offsetHeight == 0;
		return !this.domElement.isConnected;
	}

	// pointer
	onPointerDown = (event: PointerEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnPointerDown({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, false);
		}

		if (this.currentDrag == null) {
			this.currentDrag = new MapDragInfo({
				button: event.button,
				startPos_mouse: mousePos,
				startPos_world: mousePos_world,
				currentPos_mouse: mousePos,
				currentPos_world: mousePos_world,
				extras: {prevMousePos: mousePos},
			});
		}
	};
	private setMousePos_500ms = _.throttle((mapState: any, mousePos: Vec2, mousePos_world: Vec3)=>{
		RunInAction("MapControls.setMousePos_500ms", ()=>{
			mapState.mousePos_500ms = mousePos;
			mapState.mousePos_world_500ms = mousePos_world;
		});
	}, 500, {leading: true, trailing: true});
	onPointerMove = (event: PointerEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mapState = GetMapState.CatchBail(null, GetOpenMapID.CatchBail(null));
		if (mapState == null) return;

		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);

		RunInAction("MapControls.onPointerMove", ()=>{
			mapState.mousePos_0ms = mousePos;
			mapState.mousePos_world_0ms = mousePos_world;
		});
		this.setMousePos_500ms(mapState, mousePos, mousePos_world);

		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnPointerMove({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return void this.AbsorbEvent(event, false);
		}
	};
	onPointerUp = (event: PointerEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnPointerUp({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return void this.AbsorbEvent(event, false);
		}

		if (this.currentDrag?.button == event.button) {
			const countAsClick = mousePos.DistanceTo(this.currentDrag.startPos_mouse) < 5;
			if (countAsClick) {
				if (tool) {
					const subresult = tool.Scene_OnPointerClick({event, mousePos, mousePos_world, controls: this});
					if (subresult?.absorb) return this.AbsorbEvent(event, false);
				}

				if (this.options.onClick && this.domElement) {
					const worldPos = dom.ClientPosToWorldPos(mousePos);
					this.options.onClick(new MapSurfaceClickEvent({button: event.button, worldPos}));
				}
			}
		}
	};
	onPointerUp_global = (event: PointerEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnPointerUp({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return void this.AbsorbEvent(event, false);
		}

		if (this.currentDrag?.button == event.button) {
			this.currentDrag = null;
		}
	};
	onPointerMove_global = (event: PointerEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnPointerMove({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, false);
		}

		if (this.currentDrag?.button == 1) {
			const adjustedPanSpeed = this.options.panSpeed / this.ZoomLevel_Transformed;
			const panDelta = new Vec2((event.clientX - this.currentDrag.extras.prevMousePos.x) * -1, event.clientY - this.currentDrag.extras.prevMousePos.y).Times(adjustedPanSpeed);
			if (this.options.damping) {
				this.targetPosition = this.targetPosition.Plus(panDelta); // update target position for smooth damping
			} else {
				this.ApplyPan(panDelta);
			}
			this.currentDrag.extras.prevMousePos = mousePos;
		}
	};
	ApplyPan(panDelta: Vec2) {
		const panDelta_3JVec = panDelta.To3JPos();
		// Directly update camera position
		this.camera.position.x += panDelta_3JVec.x;
		this.camera.position.y += panDelta_3JVec.y;
		// Keep camera target aligned with camera position
		this.postCamPosChanged();
	}

	// mouse
	onMouseWheel = (event: WheelEvent)=>{
		const dom = this.DOM;
		if (!dom) return;
		const mousePos = new Vec2(event.clientX, event.clientY);
		const mousePos_world = dom.ClientPosToWorldPos(mousePos);
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnMouseWheel({event, mousePos, mousePos_world, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, false);
		}

		event.preventDefault();

		const worldPosBeforeZoom = dom.ClientPosToWorldPos(mousePos);

		// Determine zoom direction
		const zoomDelta = this.options.zoomSpeed * (event.deltaY < 0 ? 1 : -1); // scroll-wheel-up = -100, scroll-wheel-down = +100

		if (this.options.damping) {
			// Update target zoom for smooth damping
			this.targetZoomLevel = (this.targetZoomLevel + zoomDelta).KeepBetween(this.options.minZoom, this.options.maxZoom);
		} else {
			// Directly update zoom
			this.zoomLevel = (this.zoomLevel + zoomDelta).KeepBetween(this.options.minZoom, this.options.maxZoom);
			this.postZoomLevelChanged();
		}

		const worldPosAfterZoom = dom.ClientPosToWorldPos(mousePos);
		const zoomOffset = worldPosBeforeZoom.Minus(worldPosAfterZoom);

		if (this.options.damping) {
			this.targetPosition = this.targetPosition.Plus(zoomOffset);
		} else {
			this.camera.position.x += zoomOffset.To3JPos().x;
			this.camera.position.y += zoomOffset.To3JPos().y;
			this.postCamPosChanged();
		}
	};

	// touch
	onTouchStart = (event: TouchEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnTouchStart({event, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, true);
		}
	};
	onTouchMove_global = (event: TouchEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnTouchMove({event, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, true);
		}
	};
	onTouchEndOrCancel_global = (event: TouchEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnTouchEnd({event, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, false);
		}
	};

	// keys
	onKeyDown = (event: KeyboardEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnKeyDown({event, controls: this});
			if (subresult?.absorb) return void this.AbsorbEvent(event, false);
		}
	};
	onKeyUp = (event: KeyboardEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Scene_OnKeyUp({event, controls: this});
			if (subresult?.absorb) return void this.AbsorbEvent(event, false);
		}
	};
	onKeyDown_global = (event: KeyboardEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnKeyDown({event, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, true);
		}

		if (event.key.toLowerCase() == "f" && document.activeElement?.tagName != "INPUT" && document.activeElement?.tagName != "TEXTAREA") {
			(async()=>{
				const mapID = GetOpenMapID.CatchBail(null);
				const mapState = GetMapState.CatchBail(null, mapID);
				if (mapID == null || mapState == null) return;

				/*const activeObj = GetMapObject.CatchBail(null, mapState.activeObject);
				if (activeObj == null) return;
				const activeObj_pos_world = ResolveObjPosInWorld.CatchBail(null, activeObj);
				if (activeObj_pos_world == null) return;
				if (MapSurface_current == null) return;
				MapSurface_current.FocusWorldPos(activeObj_pos_world);*/

				const selectedObjectPositions = await GetAsync(()=>{
					return mapState.selectedObjects?.map(id=>{
						const obj = GetMapObject(id, mapID);
						if (obj == null) return null;
						const objPos_world = ResolveObjPosInWorld(obj);
						return objPos_world.ToVec2();
					}).filter(a=>a != null) as Vec2[];
				});
				if (selectedObjectPositions.length == 0) return;
				/*const selectedObjects_rect = new Rect(selectedObjectPositions[0], Vec2.zero);
				for (const obj of selectedObjectPositions.slice(1)) {
					selectedObjects_rect.Encapsulate(new Rect(selectedObjectPositions, Vec2.zero));
				}*/
				const selectedObjects_rect = selectedObjectPositions.reduce((rect, objPos)=>rect.Encapsulating(new Rect(objPos, Vec2.zero, true)), new Rect(selectedObjectPositions[0], Vec2.zero, true));
				if (MapSurface_current == null) return;
				MapSurface_current.FocusWorldPos(selectedObjects_rect.Center.ToVec3(0));

				// todo: change this someday to also change the zoom -- making the view just large enough to fit all the selected objects
			})();
		}
	};
	onKeyUp_global = (event: KeyboardEvent)=>{
		if (this.IgnoreDOMEvents()) return;
		const tool = ActiveMapTool();
		if (tool) {
			const subresult = tool.Document_OnKeyUp({event, controls: this});
			if (subresult?.absorb) return this.AbsorbEvent(event, false);
		}
	};

	AbsorbEvent(event: PointerEvent | WheelEvent | TouchEvent | KeyboardEvent, preventDefault: boolean, stopPropagation = true) {
		if (preventDefault) event.preventDefault();
		if (stopPropagation) event.stopPropagation();
	}

	postZoomLevelChanged() {
		this.camera.zoom = this.ZoomLevel_Transformed;
		this.camera.updateProjectionMatrix();

		this.options.onCamTransformChanged?.(Vec3.From3JPos(this.camera.position), this.zoomLevel);
		Invalidate3JS(this.options.invalidate ?? null, "mapControls.zoom", "camera zoom changed");
	}
	postCamPosChanged() {
		this.options.onCamTransformChanged?.(Vec3.From3JPos(this.camera.position), this.zoomLevel);
		Invalidate3JS(this.options.invalidate ?? null, "mapControls.pan", "camera position changed");
	}

	update() {
		if (!this.options.damping) return;

		// Apply damping to zoom
		if (Math.abs(this.zoomLevel - this.targetZoomLevel) > 0.001) {
			this.zoomLevel += (this.targetZoomLevel - this.zoomLevel) * this.options.dampingFactor;
			this.postZoomLevelChanged();
		}

		// Apply damping to position
		const posChangeFromDampingToApply = new Vec2(this.targetPosition.x - this.camera.position.x, this.targetPosition.y - this.camera.position.y).Times(this.options.dampingFactor);

		if (Math.abs(posChangeFromDampingToApply.x) > 0.001 || Math.abs(posChangeFromDampingToApply.y) > 0.001) {
			const posChangeFromDampingToApply_3JVec = posChangeFromDampingToApply.To3JPos();
			this.camera.position.x += posChangeFromDampingToApply_3JVec.x;
			this.camera.position.y += posChangeFromDampingToApply_3JVec.y;
			this.postCamPosChanged();
		}
	}

	// Add this for clean disposal
	dispose() {
		this.removeEventListeners();
	}

	// Additional methods for public API

	setZoom(zoomLevel: number) {
		this.targetZoomLevel = zoomLevel.KeepBetween(this.options.minZoom, this.options.maxZoom);

		if (!this.options.damping) {
			this.zoomLevel = this.targetZoomLevel;
			this.postZoomLevelChanged();
		}
	}

	panTo(pos: Vec2) {
		this.targetPosition = pos;

		if (!this.options.damping) {
			const pos_3JVec = pos.To3JPos();
			this.camera.position.x = pos_3JVec.x;
			this.camera.position.y = pos_3JVec.y;
			this.postCamPosChanged();
		}
	}

	// Get current camera view state
	/*getState() {
		return {
			position: {
				x: this.camera.position.x,
				y: this.camera.position.y,
				z: this.camera.position.z
			},
			zoom: this.zoomLevel
		};
	}*/
}